import type { FC, ReactNode, RefObject } from 'react'

import { useId } from 'react'
import { ArrowUp } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ChatComposerProps {
    value: string
    onChange: (value: string) => void
    onSubmit: () => void
    label: string
    placeholder?: string
    disabled?: boolean
    type?: 'text' | 'email'
    size?: 'lg' | 'sm'
    onFocus?: () => void
    inputRef?: RefObject<HTMLInputElement | null>
    className?: string
    sendLabel?: string
}

/** Rounded input bar with a round send button. Used for chat messages and the inline email field. */
export const ChatComposer: FC<ChatComposerProps> = ({
    value,
    onChange,
    onSubmit,
    label,
    placeholder = 'Start typing...',
    disabled = false,
    type = 'text',
    size = 'lg',
    onFocus,
    inputRef,
    className,
    sendLabel = 'Send message'
}): ReactNode => {
    const id = useId()
    const small = size === 'sm'

    return (
        <form
            noValidate
            onSubmit={event => {
                event.preventDefault()
                if (!disabled) onSubmit()
            }}
            className={cn(
                'flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] transition-colors focus-within:border-white/25 focus-within:bg-white/[0.06]',
                small ? 'p-1 pl-4' : 'p-1.5 pl-5',
                className
            )}
        >
            <label htmlFor={id} className='sr-only'>
                {label}
            </label>

            <input
                ref={inputRef}
                id={id}
                type={type}
                value={value}
                disabled={disabled}
                onChange={event => onChange(event.target.value)}
                onFocus={onFocus}
                placeholder={placeholder}
                autoComplete={type === 'email' ? 'email' : 'off'}
                inputMode={type === 'email' ? 'email' : undefined}
                maxLength={type === 'email' ? 200 : 1000}
                className={cn(
                    'min-w-0 flex-1 bg-transparent text-white placeholder:text-white/35 focus:outline-none disabled:opacity-60',
                    small ? 'h-8 text-base sm:text-sm' : 'h-10 text-base sm:text-[15px]'
                )}
            />

            <button
                type='submit'
                aria-label={sendLabel}
                disabled={disabled || !value.trim()}
                className={cn(
                    'grid shrink-0 place-items-center rounded-full bg-white/[0.13] text-white transition-colors duration-200 hover:bg-white/[0.22] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 disabled:cursor-not-allowed disabled:opacity-40',
                    small ? 'size-8' : 'size-10'
                )}
            >
                <ArrowUp aria-hidden='true' className={small ? 'size-3.5' : 'size-4'} />
            </button>
        </form>
    )
}
